<template>
  <div class="platform-regions-picker">
    <BasePillGroup is-small multiple :options="options" :value="selectedCodes" @input="onInput" />
    <p v-if="value.length" class="platform-regions-picker__names">{{ selectedNames }}</p>
    <p v-if="hint" class="platform-regions-picker__hint">{{ hint }}</p>
  </div>
</template>

<script>
import BasePillGroup from '@/components/base/BasePillGroup';
import { PLATFORM_REGIONS } from '@/util/constants/platform-regions';
import { LOCALE } from '@/util/i18n/locale';

/**
 * Region codes as toggle pills. `value` is the platform's `[{ code, nameFr, nameEn }]`; `change`
 * emits the new list in pill order. A stored region missing from PLATFORM_REGIONS still gets a pill
 * (with its stored names) so editing a platform never drops it silently.
 */
export default {
  name: 'PlatformRegionsPicker',
  components: { BasePillGroup },
  props: {
    value: { type: Array, default: () => [] },
    hint: { type: String, default: null },
  },
  computed: {
    regions() {
      const known = new Set(PLATFORM_REGIONS.map((region) => region.code));
      const unknown = this.value.filter((region) => !known.has(region.code));
      return [...PLATFORM_REGIONS, ...unknown];
    },
    options() {
      return this.regions.map((region) => ({ value: region.code, label: region.code }));
    },
    selectedCodes() {
      return this.value.map((region) => region.code);
    },
    selectedNames() {
      const isFrench = this.$i18n.locale === LOCALE.FR_CA;
      return this.value.map((region) => (isFrench ? region.nameFr : region.nameEn)).join(', ');
    },
  },
  methods: {
    onInput(codes) {
      const stored = new Map(this.value.map((region) => [region.code, region]));
      const next = this.regions
        .filter((region) => codes.includes(region.code))
        .map((region) => stored.get(region.code) || { ...region });
      this.$emit('change', next);
    },
  },
};
</script>

<style lang="scss">
.platform-regions-picker {
  .base-pill-group {
    flex-wrap: wrap;
  }

  &__names {
    margin: 6px 0 0;
    color: $color-normal;
    font-size: 13px;
  }

  &__hint {
    margin: 6px 0 0;
    color: $color-text-body;
    font-size: 12px;
    line-height: 1.4;
  }
}
</style>
